/**
 * Epic LOCAIS-POR-CLIENTE — Story 7: hook central que gerencia o estado
 * dos locais de contagem agrupados por cliente na tela de criar contagem.
 *
 * Fonte da verdade: `locaisPorCliente: Record<clienteId, LocalContagem[]>`.
 *
 * Responsabilidades:
 * - Buscar o preview agrupado (`GET /jobs/preview-locais-por-cliente`) quando
 *   hospital + clientes mudam.
 * - Auto-seed: ao carregar preview pela primeira vez em modo "novo job",
 *   pré-selecionar TODOS os locais retornados pelo backend.
 * - Em modo edit, aceitar seed explícito via `seedFromJobResponse` (novo formato)
 *   ou `seedFromLegacyLocais` (formato antigo — lista flat sem cliente).
 * - Expor actions para toggle/add/remove de locais por cliente.
 * - Computar `isLocalShared` e `getClienteLocaisPayload` para o save hook.
 *
 * Uso:
 * ```
 * const locaisHook = useContagemLocaisPorCliente(hospitalId, clienteIds);
 * // passe locaisHook.getClienteLocaisPayload() para o payload do POST /jobs
 * ```
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import jobService from '../services/jobService';
import type { LocalContagem } from '../types/estabelecimento';
import type {
  ClienteLocaisPayload,
  ClienteLocaisPreview,
  LocalDoCliente,
} from '../types/contagemLocais';
import { localDoClienteToLocalContagem } from '../types/contagemLocais';

export interface UseContagemLocaisPorClienteReturn {
  // state
  locaisPorCliente: Record<number, LocalContagem[]>;
  previewData: ClienteLocaisPreview[];
  loadingPreview: boolean;
  errorPreview: string | null;

  // actions
  toggleLocal: (clienteId: number, local: LocalContagem) => void;
  setLocaisParaCliente: (clienteId: number, locais: LocalContagem[]) => void;
  adicionarLocalExtra: (clienteId: number, local: LocalContagem) => void;
  removerLocal: (clienteId: number, localId: number) => void;
  seedFromJobResponse: (payload: ClienteLocaisPayload[], hospitalId: number) => Promise<void>;
  seedFromLegacyLocais: (locais: LocalContagem[], firstClienteId: number) => void;
  resetSeed: () => void;

  // computed / derived
  isLocalShared: (localId: number) => boolean;
  totalLocaisSelecionados: number;
  getClienteLocaisPayload: () => ClienteLocaisPayload[];
  getAllLocaisFlat: () => LocalContagem[];
}

export interface UseContagemLocaisPorClienteOptions {
  /** Callback invoked whenever the state changes (for parent modification tracking). */
  onModification?: () => void;
}

export const useContagemLocaisPorCliente = (
  selectedHospitalId: number,
  selectedClienteIds: number[],
  options: UseContagemLocaisPorClienteOptions = {},
): UseContagemLocaisPorClienteReturn => {
  const [locaisPorCliente, setLocaisPorCliente] = useState<Record<number, LocalContagem[]>>({});
  const [previewData, setPreviewData] = useState<ClienteLocaisPreview[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [errorPreview, setErrorPreview] = useState<string | null>(null);

  // initializedFromJob blocks auto-seed when the user is editing an existing job
  const initializedFromJobRef = useRef(false);
  const onModificationRef = useRef(options.onModification);
  onModificationRef.current = options.onModification;

  const notifyModification = useCallback(() => {
    onModificationRef.current?.();
  }, []);

  // Serialize clienteIds to a stable string for effect deps (avoids re-renders by reference).
  const clienteIdsKey = useMemo(
    () => [...selectedClienteIds].sort((a, b) => a - b).join(','),
    [selectedClienteIds],
  );

  // ─── Effect: fetch preview whenever hospital + clientes change ─────────────
  useEffect(() => {
    let cancelled = false;

    const fetchPreview = async () => {
      if (!selectedHospitalId || selectedClienteIds.length === 0) {
        setPreviewData([]);
        // Clean up state for clientes that are no longer selected (NOT an early return —
        // we still want to drop stale entries from locaisPorCliente)
        setLocaisPorCliente((prev) => {
          const next: Record<number, LocalContagem[]> = {};
          Object.keys(prev).forEach((k) => {
            const cid = Number(k);
            if (selectedClienteIds.includes(cid)) next[cid] = prev[cid];
          });
          return next;
        });
        return;
      }

      setLoadingPreview(true);
      setErrorPreview(null);

      try {
        const data = await jobService.getPreviewLocaisPorCliente(
          selectedHospitalId,
          selectedClienteIds,
        );
        if (cancelled) return;
        setPreviewData(data);

        setLocaisPorCliente((prev) => {
          const next = { ...prev };

          // Auto-seed ONLY when not in edit-mode.
          if (!initializedFromJobRef.current) {
            // Add auto-selected locais for each cliente NOT already in state
            data.forEach((clienteGroup) => {
              if (!next[clienteGroup.cliente_id]) {
                next[clienteGroup.cliente_id] = clienteGroup.locais.map(
                  (l: LocalDoCliente) => localDoClienteToLocalContagem(l),
                );
              }
            });
          }

          // T18: SEMPRE remover clientes que saíram da seleção (criar E editar).
          // Antes esse cleanup ficava dentro do guard de auto-seed, então no modo
          // EDITAR os "locais planejados" de um cliente removido continuavam no
          // estado e apareciam no agendamento mesmo após remover o cliente.
          Object.keys(next).forEach((k) => {
            if (!selectedClienteIds.includes(Number(k))) {
              delete next[Number(k)];
            }
          });

          return next;
        });
      } catch (err: any) {
        if (cancelled) return;
        console.error('[useContagemLocaisPorCliente] Erro ao buscar preview:', err);
        setErrorPreview(err?.response?.data?.detail || err?.message || 'Erro ao carregar locais');
        setPreviewData([]);
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHospitalId, clienteIdsKey]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const toggleLocal = useCallback(
    (clienteId: number, local: LocalContagem) => {
      setLocaisPorCliente((prev) => {
        const current = prev[clienteId] || [];
        const isSelected = current.some((l) => l.id === local.id);
        return {
          ...prev,
          [clienteId]: isSelected
            ? current.filter((l) => l.id !== local.id)
            : [...current, local],
        };
      });
      notifyModification();
    },
    [notifyModification],
  );

  const setLocaisParaCliente = useCallback(
    (clienteId: number, locais: LocalContagem[]) => {
      setLocaisPorCliente((prev) => ({ ...prev, [clienteId]: locais }));
      notifyModification();
    },
    [notifyModification],
  );

  const adicionarLocalExtra = useCallback(
    (clienteId: number, local: LocalContagem) => {
      setLocaisPorCliente((prev) => {
        const current = prev[clienteId] || [];
        if (current.some((l) => l.id === local.id)) return prev;
        return { ...prev, [clienteId]: [...current, local] };
      });
      notifyModification();
    },
    [notifyModification],
  );

  const removerLocal = useCallback(
    (clienteId: number, localId: number) => {
      setLocaisPorCliente((prev) => {
        const current = prev[clienteId] || [];
        return { ...prev, [clienteId]: current.filter((l) => l.id !== localId) };
      });
      notifyModification();
    },
    [notifyModification],
  );

  /** Seed state from the new job response format (cliente_locais grouped).
   *
   * Needs the hospital's full LocalContagem cache to hydrate name/responsavel.
   * Fetches it from the estabelecimentos endpoint. Blocks subsequent auto-seeds. */
  const seedFromJobResponse = useCallback(
    async (payload: ClienteLocaisPayload[], hospitalId: number) => {
      initializedFromJobRef.current = true;
      if (!hospitalId || payload.length === 0) {
        setLocaisPorCliente({});
        return;
      }

      // Fetch all locais from the hospital to hydrate LocalContagem objects
      try {
        const allLocais = await jobService.getLocaisContagem(hospitalId);
        const localById = new Map<number, LocalContagem>();
        allLocais.forEach((l: any) => {
          localById.set(l.id, {
            id: l.id,
            local: l.nome || l.local || '',
            responsavel: l.responsavel || '',
            telefone: '',
            ramal: '',
            email: '',
            ativo: true,
          });
        });

        const next: Record<number, LocalContagem[]> = {};
        payload.forEach((entry) => {
          const locais = entry.local_ids
            .map((lid) => localById.get(lid))
            .filter((l): l is LocalContagem => !!l);
          next[entry.cliente_id] = locais;
        });
        setLocaisPorCliente(next);
      } catch (err) {
        console.error('[useContagemLocaisPorCliente] Erro ao seedFromJobResponse:', err);
      }
    },
    [],
  );

  /** Seed state from legacy flat list — groups everything under the first cliente.
   *
   * Used for contagens antigas that only have `locais_contagem` (M2M plano)
   * without the grouped cliente_locais. User can reorganize manually by
   * moving locais to other clientes; on save the data migrates automatically. */
  const seedFromLegacyLocais = useCallback(
    (locais: LocalContagem[], firstClienteId: number) => {
      initializedFromJobRef.current = true;
      if (!firstClienteId) {
        setLocaisPorCliente({});
        return;
      }
      setLocaisPorCliente({ [firstClienteId]: locais });
    },
    [],
  );

  const resetSeed = useCallback(() => {
    initializedFromJobRef.current = false;
  }, []);

  // ─── Computed / Derived ────────────────────────────────────────────────────

  /** Fast lookup: how many clientes have this local selected? */
  const sharedCountMap = useMemo(() => {
    const map = new Map<number, number>();
    Object.keys(locaisPorCliente).forEach((k) => {
      const arr = locaisPorCliente[Number(k)];
      arr.forEach((l) => {
        map.set(l.id, (map.get(l.id) || 0) + 1);
      });
    });
    return map;
  }, [locaisPorCliente]);

  const isLocalShared = useCallback(
    (localId: number) => (sharedCountMap.get(localId) || 0) >= 2,
    [sharedCountMap],
  );

  const totalLocaisSelecionados = useMemo(() => {
    const seen = new Set<number>();
    Object.keys(locaisPorCliente).forEach((k) => {
      locaisPorCliente[Number(k)].forEach((l) => seen.add(l.id));
    });
    return seen.size;
  }, [locaisPorCliente]);

  const getClienteLocaisPayload = useCallback(
    (): ClienteLocaisPayload[] =>
      Object.keys(locaisPorCliente).map((k) => ({
        cliente_id: Number(k),
        local_ids: locaisPorCliente[Number(k)].map((l) => l.id),
      })),
    [locaisPorCliente],
  );

  const getAllLocaisFlat = useCallback((): LocalContagem[] => {
    const seen = new Set<number>();
    const out: LocalContagem[] = [];
    Object.keys(locaisPorCliente).forEach((k) => {
      locaisPorCliente[Number(k)].forEach((l) => {
        if (!seen.has(l.id)) {
          seen.add(l.id);
          out.push(l);
        }
      });
    });
    return out;
  }, [locaisPorCliente]);

  return {
    locaisPorCliente,
    previewData,
    loadingPreview,
    errorPreview,
    toggleLocal,
    setLocaisParaCliente,
    adicionarLocalExtra,
    removerLocal,
    seedFromJobResponse,
    seedFromLegacyLocais,
    resetSeed,
    isLocalShared,
    totalLocaisSelecionados,
    getClienteLocaisPayload,
    getAllLocaisFlat,
  };
};

export default useContagemLocaisPorCliente;
